// Maps to backend/routers/shared_resources.py
import { useState, useEffect, useCallback } from 'react';
import { env } from '../utils/env';

export interface SharedMember {
  id: string;
  email: string;
  name?: string;
  status: 'pending' | 'active' | 'declined';
  joined_at?: string;
}

// Centralize API calls
const sharedApi = {
  getMembers: async (type: 'account' | 'budget', resourceId: string): Promise<SharedMember[]> => {
    // Skip the API call if resourceId is empty
    if (!resourceId) {
      return [];
    }

    try {
      let apiUrl = '';
      if (type === 'account') {
        apiUrl = `${env.API_URL}/api/shared/accounts/${resourceId}/members`;
      } else { // type === 'budget'
        // For budget plan IDs, don't make the network request at all
        // Budget plans are user-specific and not shared resources
        if (!isNaN(parseInt(resourceId))) {
          console.info(`Skipping members API call for budget plan ${resourceId} - budget plans are not shared`);
          return [];
        }
        apiUrl = `${env.API_URL}/api/shared/budgets/categories/${resourceId}/members`;
      }

      console.info(`Fetching members from ${apiUrl}`);
      const response = await fetch(
        apiUrl,
        {
          credentials: 'include',
          // Ensure proper headers, including Authorization, are sent
          headers: {
            'Accept': 'application/json',
          }
        }
      );

      if (response.status === 401) {
        console.warn(`Authentication error (401) when fetching ${type} members for ID ${resourceId}`);
        // Return empty array without throwing an error for 401s
        return [];
      }

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { detail: errorText };
        }

        console.error(`Error fetching ${type} members:`, errorData);

        // Special case for budget plans or categories that aren't shared
        if (type === 'budget' && (response.status === 404 || response.status === 403)) {
          console.info(`Budget ${resourceId} not found or not accessible (${response.status}) - treating as empty`);
          return []; // Return empty array for budget plans
        }

        throw new Error(errorData.detail || `Failed to fetch ${type} members`);
      }

      const data = await response.json();
      console.info(`Successfully fetched ${data.length} members for ${type} ${resourceId}`);
      return data;
    } catch (error) {
      console.error(`API error in getMembers for ${type} ${resourceId}:`, error);
      return []; // Return empty array on error to prevent UI from breaking
    }
  },

  shareResource: async (type: 'account' | 'budget', resourceId: string, email: string): Promise<void> => {
    let apiUrl = '';
    if (type === 'account') {
      apiUrl = `${env.API_URL}/api/shared/accounts/${resourceId}/share`;
    } else { // type === 'budget'
      apiUrl = `${env.API_URL}/api/shared/budgets/categories/${resourceId}/share`;
    }
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Failed to share resource');
    }
  },

  removeMember: async (type: 'account' | 'budget', resourceId: string, memberId: string): Promise<void> => {
    let apiUrl = '';
    if (type === 'account') {
      apiUrl = `${env.API_URL}/api/shared/accounts/${resourceId}/members/${memberId}`;
    } else { // type === 'budget'
      apiUrl = `${env.API_URL}/api/shared/budgets/categories/${resourceId}/members/${memberId}`;
    }
    const response = await fetch(
      apiUrl,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Failed to remove member');
    }
  },
};

export const useSharedResource = (type: 'account' | 'budget', resourceId: string) => {
  const [members, setMembers] = useState<SharedMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await sharedApi.getMembers(type, resourceId);
      setMembers(data);
    } catch (err) {
      console.error(`Error in fetchMembers for ${type} ${resourceId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch members');
      // Don't throw the error, just set empty members to avoid breaking the UI
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [type, resourceId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleShare = async (email: string) => {
    try {
      setError(null);
      await sharedApi.shareResource(type, resourceId, email);
      await fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share');
      throw err;
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      setError(null);
      await sharedApi.removeMember(type, resourceId, memberId);
      await fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
      throw err;
    }
  };

  return {
    members,
    loading,
    error,
    handleShare,
    handleRemoveMember,
    refreshMembers: fetchMembers,
  };
}; 